import type { DashboardCell, DashboardStatusCategory } from '@myapp/contract'
import { DASHBOARD_CATEGORY_ORDER } from './statusCategory'

/** The value `gewerke` filters carry for Aufträge Immotion could not assign a Gewerk to. */
export const NO_GEWERK_KEY = 'none'
export const NO_GEWERK_LABEL = 'Ohne Gewerk'

export type DashboardMetric = 'count' | 'cost' | 'hours'

export const gewerkKeyOf = (cell: Pick<DashboardCell, 'gewerkId'>): string =>
  cell.gewerkId == null ? NO_GEWERK_KEY : String(cell.gewerkId)

export interface DashboardCellFilter {
  gewerke: readonly string[]
  status: DashboardStatusCategory | null
}

/**
 * Cells that pass the Gewerk and status filters. A panel passes the filter it is itself the
 * control for in `skip`, so selecting a bar dims the others instead of making them vanish.
 */
export function filterCells(
  cells: readonly DashboardCell[],
  filter: DashboardCellFilter,
  skip: ReadonlyArray<'gewerk' | 'status'> = [],
): DashboardCell[] {
  const useGewerk = !skip.includes('gewerk') && filter.gewerke.length > 0
  const useStatus = !skip.includes('status') && filter.status != null
  return cells.filter(
    (c) =>
      (!useGewerk || filter.gewerke.includes(gewerkKeyOf(c))) &&
      (!useStatus || c.category === filter.status),
  )
}

const sum = (cells: readonly DashboardCell[], pick: (c: DashboardCell) => number) =>
  cells.reduce((s, c) => s + pick(c), 0)

export interface DashboardKpis {
  orders: number
  completed: number
  open: number
  sollEuro: number
  istEuro: number
  /** Ist of Soll in percent; 0 when there is no Soll. */
  euroRatio: number
}

export function selectKpis(cells: readonly DashboardCell[]): DashboardKpis {
  const orders = sum(cells, (c) => c.orderCount)
  const completed = sum(
    cells.filter((c) => c.category === 'COMPLETED'),
    (c) => c.orderCount,
  )
  const sollEuro = sum(cells, (c) => c.sollEuro)
  const istEuro = sum(cells, (c) => c.istEuro)
  return {
    orders,
    completed,
    open: orders - completed,
    sollEuro,
    istEuro,
    euroRatio: sollEuro > 0 ? (istEuro / sollEuro) * 100 : 0,
  }
}

export interface GewerkRow {
  key: string
  name: string
  total: number
  byCategory: Record<DashboardStatusCategory, number>
}

const emptyByCategory = (): Record<DashboardStatusCategory, number> => ({
  COMPLETED: 0,
  PLANNED: 0,
  NOT_PLANNED: 0,
  FOLLOW_UP_REQUIRED: 0,
  NOT_REACHED: 0,
})

/** One row per Gewerk, biggest first. */
export function selectGewerkRows(cells: readonly DashboardCell[]): GewerkRow[] {
  const rows = new Map<string, GewerkRow>()
  for (const cell of cells) {
    const key = gewerkKeyOf(cell)
    let row = rows.get(key)
    if (!row) {
      row = { key, name: cell.gewerkName ?? NO_GEWERK_LABEL, total: 0, byCategory: emptyByCategory() }
      rows.set(key, row)
    }
    row.total += cell.orderCount
    row.byCategory[cell.category] += cell.orderCount
  }
  return Array.from(rows.values()).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'de'))
}

export interface StatusSlice {
  category: DashboardStatusCategory
  count: number
  /** Share of all Aufträge in percent. */
  share: number
}

/** Every category in the fixed order, including those with no Aufträge. */
export function selectStatusSlices(cells: readonly DashboardCell[]): StatusSlice[] {
  const total = sum(cells, (c) => c.orderCount)
  return DASHBOARD_CATEGORY_ORDER.map((category) => {
    const count = sum(
      cells.filter((c) => c.category === category),
      (c) => c.orderCount,
    )
    return { category, count, share: total > 0 ? (count / total) * 100 : 0 }
  })
}

export interface GewerkOption {
  value: string
  label: string
}

/** Gewerke that occur in the data, by name, with "Ohne Gewerk" last. */
export function selectGewerkOptions(cells: readonly DashboardCell[]): GewerkOption[] {
  const seen = new Map<string, string>()
  for (const cell of cells) {
    const key = gewerkKeyOf(cell)
    if (!seen.has(key)) seen.set(key, cell.gewerkName ?? NO_GEWERK_LABEL)
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === NO_GEWERK_KEY) return 1
      if (b.value === NO_GEWERK_KEY) return -1
      return a.label.localeCompare(b.label, 'de')
    })
}

export interface MonthlyPoint {
  month: string
  soll: number
  ist: number
  orderCount: number
}

/**
 * One point per month in `months`, also for months without data. Hours are returned as hours,
 * not minutes. For `count`, Ist is the Aufträge that reached "Abgeschlossen".
 */
export function selectMonthlySeries(
  cells: readonly DashboardCell[],
  months: readonly string[],
  metric: DashboardMetric,
): MonthlyPoint[] {
  return months.map((month) => {
    const inMonth = cells.filter((c) => c.month === month)
    const orderCount = sum(inMonth, (c) => c.orderCount)
    if (metric === 'count') {
      const completed = sum(
        inMonth.filter((c) => c.category === 'COMPLETED'),
        (c) => c.orderCount,
      )
      return { month, soll: orderCount, ist: completed, orderCount }
    }
    if (metric === 'cost') {
      return { month, soll: sum(inMonth, (c) => c.sollEuro), ist: sum(inMonth, (c) => c.istEuro), orderCount }
    }
    return {
      month,
      soll: sum(inMonth, (c) => c.plannedMinutes) / 60,
      ist: sum(inMonth, (c) => c.actualMinutes) / 60,
      orderCount,
    }
  })
}

export interface YearSummary {
  year: number
  soll: number
  ist: number
}

export function selectYearSummary(series: readonly MonthlyPoint[]): YearSummary[] {
  const byYear = new Map<number, YearSummary>()
  for (const point of series) {
    const year = Number(point.month.slice(0, 4))
    const entry = byYear.get(year) ?? { year, soll: 0, ist: 0 }
    entry.soll += point.soll
    entry.ist += point.ist
    byYear.set(year, entry)
  }
  return Array.from(byYear.values()).sort((a, b) => a.year - b.year)
}
